import { Component, OnInit, OnDestroy, Input, EventEmitter, Output, SimpleChanges, OnChanges, inject } from '@angular/core';
import { CommonModule, TitleCasePipe } from '@angular/common';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { Subject, Subscription, timer } from 'rxjs';
import { takeUntil, distinctUntilChanged, take } from 'rxjs/operators';
import { InputForm } from 'ustore-ui';

import { ToastService } from '../../../shared/services/toast.service';
import { CardWrapperComponent } from '../../../shared/components/card-wrapper/card-wrapper.component';
import {
  SlideButtonComponent,
  SlideButtonOption,
} from '../../../shared/components/slide-button/slide-button.component';
import { GetCardsData } from '../../../core/models/card.model';
import { CreditCardListComponent } from '../../../shared/components/credit-card-list/credit-card-list.component';
import { cardFlag } from '../../../shared/components/credit-card-item/credit-card-item.component';
import { InputCreditCardNumberComponent } from '../../../shared/components/input-credit-card-number/input-credit-card-number.component';
import { expiryDateValidator } from '../../../shared/validators/expiry-date.validator';
import { cvcValidator } from '../../../shared/validators/cvc.validator';
import { CustomValidators } from '../../../shared/validators/custom-validators';
import { cardNameValidator } from '../../../shared/validators/card-name.validator';
import { ValidationMessagePipe } from '../../../shared/pipes/validation-message.pipe';
import { CnpjPipe } from '../../../shared/pipes/cnpj.pipe';
import { ExpiryDatePipe } from '../../../shared/pipes/expiry-date.pipe';
import { CardNamePipe } from '../../../shared/pipes/card-name.pipe';
import { unformatCnpj } from '../../../core/utils/cnpj.util';
import { SellerResponse } from '../../../core/models/seller.model';
import { SellerService } from '../../../core/services/seller.service';
import { CreditCardService } from '../../../core/services/credit-card.service';
import { UserFacade } from '../../../core/store/user/user.facade';
import { ExternalCreditCardService } from '../../../core/services/external-credit-card.service';
import { ExternalCreditCardRequest } from '../../../core/models/external-credit-card.model';
import { DeviceInfoService } from '../../../core/services/device-info.service';
import { DeviceInfo } from '../../../core/models/device-info.model';
import { User } from '../../../core/models/user.model';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { PaymentOptionService } from '../../../shared/services/payment-option.service';
import { I18nPipe } from '../../../shared/pipes/i18n.pipe';
import { I18nService } from '../../../core/services/i18n.service';
import { CartFacade } from '../../../core/store/cart/cart.facade';
import { CartItem } from '../../../core/store/cart/cart.state';
import { ProductV2Variation } from '../../../core/models/product-v2.model';

// TODO: reativar cartão de crédito
const CREDIT_CARD_PAYMENT_ENABLED = false;

@Component({
  selector: 'umkt-payment-invoice-card',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardWrapperComponent,
    SlideButtonComponent,
    InputForm,
    CreditCardListComponent,
    InputCreditCardNumberComponent,
    ValidationMessagePipe,
    LoadingComponent,
    TitleCasePipe,
    I18nPipe
  ],
  templateUrl: './payment-invoice-card.component.html',
  styleUrl: './payment-invoice-card.component.scss',
})
export class PaymentInvoiceCardComponent
  implements OnInit, OnChanges, OnDestroy
{
  private destroy$ = new Subject<void>();

  sellerForm: FormGroup;
  creditCardForm: FormGroup;
  cnpjPipe = new CnpjPipe();
  expiryDatePipe = new ExpiryDatePipe();
  cardNamePipe = new CardNamePipe();
  showCardForm: boolean = false;
  sellerAssistance: boolean = false;
  selectedPaymentMethod: 'boleto' | 'credit_card' =
    CREDIT_CARD_PAYMENT_ENABLED ? 'credit_card' : 'boleto';
  cardsInfo: GetCardsData[] = [];
  selectedCardId: string | 'new' = 'new';
  userUuid: string | null = null;
  userData: User | null = null;
  sellerData: SellerResponse | null = null;
  deviceInfo: DeviceInfo | null = null;
  isLoading: boolean = false;
  isLoadingSellerCode: boolean = false;
  hasDSMProducts: boolean = false;

  // backup para edicao no ultimo step
  private originalSelectedPaymentMethod: 'boleto' | 'credit_card' | null = null;
  private originalSelectedCardId: string | 'new' | null = null;
  private originalSellerAssistance: boolean | null = null;
  private originalSellerCode: string | null = null;
  private originalSellerData: SellerResponse | null = null;
  private langSubscription?: Subscription;

  @Input() isPlanPayment: boolean = false;
  @Input() currentCardToken: any;
  @Input() canGoToPreviousStep = false;
  @Input() canGoToNextStep = false;
  @Input() initiallyOpen = false;
  @Input() disabled = false;
  @Input() isEditing = true;
  @Input() invoicePaymentType: 'minha_conta' | 'boleto_cartao' =
    'boleto_cartao';
  @Input() isLastStep = false;
  @Input() initialSelectedPaymentMethod: 'boleto' | 'credit_card' | null = null;
  @Input() initialSelectedCardId: string | 'new' | null = null;
  @Input() initialSellerCode: string | null = null;
  @Input() initialSellerData: SellerResponse | null = null;
  @Output() cardFormVisibilityChange = new EventEmitter<boolean>();
  @Output() tokenCardSelected = new EventEmitter<string>();
  @Output() previousStep = new EventEmitter<void>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() editingStateChange = new EventEmitter<boolean>();
  @Output() paymentMethodChange = new EventEmitter<'boleto' | 'credit_card'>();

  private i18nService = inject(I18nService);

  paymentMethodOptions: SlideButtonOption[] = [];

  private paymentOptionService = inject(PaymentOptionService);
  private cartFacade = inject(CartFacade);
  private fb = inject(FormBuilder);
  private sellerService = inject(SellerService);
  private creditCardService = inject(CreditCardService);
  private externalCreditCardService = inject(ExternalCreditCardService);
  private userFacade = inject(UserFacade);
  private toastService = inject(ToastService);
  private deviceInfoService = inject(DeviceInfoService);

  constructor() {
    this.sellerForm = this.fb.group({
      sellerCode: [null, [Validators.required]],
    });

    this.creditCardForm = this.fb.group({
      bandeira: [null, [Validators.required]],
      document: [null, [Validators.required, CustomValidators.cnpj]],
      cardHolder: [
        null,
        [
          Validators.required,
          CustomValidators.noWhitespaceOnly,
          cardNameValidator(this.i18nService),
        ],
      ],
      cardSecurityCode: [null, [Validators.required, cvcValidator(this.i18nService)]],
      cardNumber: [null, Validators.required],
      cardExpirationDate: [null, [Validators.required, expiryDateValidator(this.i18nService)]],
    });
  }

  ngOnInit(): void {
    this.langSubscription = this.i18nService.getCurrentLanguage().subscribe(() => {
      this.updatePaymentMethodOptions();
      const cvcControl = this.creditCardForm.get('cardSecurityCode');
      if (cvcControl) {
        cvcControl.updateValueAndValidity({ emitEvent: false }); 
      }

      const holderControl = this.creditCardForm.get('cardHolder');
      if (holderControl) {
        holderControl.updateValueAndValidity({ emitEvent: false });
      }

      const expiryControl = this.creditCardForm.get('cardExpirationDate');
      if (expiryControl) {
        expiryControl.updateValueAndValidity({ emitEvent: false });
      }
    });

    this.cartFacade.getCartItems()
      .pipe(takeUntil(this.destroy$))
      .subscribe((cartItems: CartItem[]) => {
        this.updatePaymentMethodOptions(cartItems);
      });

    this.cartFacade.getCartItems()
      .pipe(take(1))
      .subscribe((cartItems: CartItem[]) => {
        this.updatePaymentMethodOptions(cartItems);
        this.validateAndAdjustPaymentMethod(cartItems);
      });

    this.setupSalespersonCodeSubscription();
    this.captureDeviceInfo();

    if (this.initialSelectedPaymentMethod !== null) {
      this.selectedPaymentMethod = this.resolvePaymentMethod(
        this.initialSelectedPaymentMethod
      );
      this.paymentOptionService.setSelected(this.selectedPaymentMethod);
      if (
        this.selectedPaymentMethod === 'credit_card' &&
        (this.initialSelectedCardId === null ||
          this.initialSelectedCardId === 'new')
      ) {
        this.selectedCardId = 'new';
        this.showCardForm = false;
      }
    } else if (this.invoicePaymentType === 'boleto_cartao') {
      const savedValue = this.paymentOptionService.selected();
      if (
        savedValue &&
        (savedValue === 'boleto' || savedValue === 'credit_card')
      ) {
        this.selectedPaymentMethod = this.resolvePaymentMethod(
          savedValue as 'boleto' | 'credit_card'
        );
        this.paymentOptionService.setSelected(this.selectedPaymentMethod);
      } else {
        this.selectedPaymentMethod = this.defaultPaymentMethod();
        this.paymentOptionService.setSelected(this.selectedPaymentMethod);
      }
    }
    if (this.initialSelectedCardId !== null) {
      this.selectedCardId = this.initialSelectedCardId;
    }

    this.userFacade.getLoginData().subscribe(loginData => {
      if (loginData && loginData.userUuid) {
        this.userUuid = loginData.userUuid;
        // Se estiver em modo de visualização e for cartão de crédito, carregar cartões
        // e se não houver produtos STARBEM
        if (
          !this.isEditing &&
          this.selectedPaymentMethod === 'credit_card' &&
          !this.isPlanPayment &&
          this.invoicePaymentType === 'boleto_cartao' &&
          !this.hasDSMProducts
        ) {
          this.loadCards();
        }
      }
    });

    this.userFacade.getUserDetails().subscribe(userData => {
      this.userData = userData;
    });

    if (this.isPlanPayment) {
      this.selectedPaymentMethod = 'credit_card';
      this.onPaymentMethodChange('credit_card');
    }
    this.applyInitialSellerToView();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentCardToken'] && this.currentCardToken) {
      this.selectedCardId = this.currentCardToken;
    }
    if (changes['invoicePaymentType']) {
      const oldValue = changes['invoicePaymentType'].previousValue;
      const newValue = changes['invoicePaymentType'].currentValue;
      this.invoicePaymentType = newValue;

      if (oldValue !== undefined && oldValue !== newValue) {
        if (newValue === 'minha_conta') {
          this.creditCardForm.reset();
          this.selectedCardId = 'new';
          this.showCardForm = false;
        } else if (oldValue === 'minha_conta' && newValue === 'boleto_cartao') {
          if (this.initialSelectedPaymentMethod !== null) {
            this.selectedPaymentMethod = this.resolvePaymentMethod(
              this.initialSelectedPaymentMethod
            );
            this.paymentOptionService.setSelected(this.selectedPaymentMethod);
          } else {
            const savedValue = this.paymentOptionService.selected();
            if (
              savedValue &&
              (savedValue === 'boleto' || savedValue === 'credit_card')
            ) {
              this.selectedPaymentMethod = this.resolvePaymentMethod(
                savedValue as 'boleto' | 'credit_card'
              );
              this.paymentOptionService.setSelected(this.selectedPaymentMethod);
            } else {
              this.selectedPaymentMethod = this.defaultPaymentMethod();
              this.paymentOptionService.setSelected(this.selectedPaymentMethod);
            }
          }
          this.selectedCardId = 'new';
          this.showCardForm = false;
          if (this.isLastStep && !this.isEditing) {
            this.isEditing = true;
          }
        }
      }
    }
    if (changes['isEditing']) {
      this.editingStateChange.emit(this.isEditing);
      if (
        !this.isEditing &&
        this.selectedPaymentMethod === 'credit_card' &&
        this.userUuid &&
        !this.isPlanPayment &&
        !this.hasDSMProducts
      ) {
        this.loadCards();
      }
    }
    if (
      changes['initialSelectedPaymentMethod'] &&
      this.initialSelectedPaymentMethod !== null
    ) {
      this.selectedPaymentMethod = this.resolvePaymentMethod(
        this.initialSelectedPaymentMethod
      );
      this.paymentOptionService.setSelected(this.selectedPaymentMethod);
      if (
        this.selectedPaymentMethod === 'credit_card' &&
        (this.initialSelectedCardId === null ||
          this.initialSelectedCardId === 'new')
      ) {
        this.selectedCardId = 'new';
        this.showCardForm = false;
        this.tokenCardSelected.emit(this.selectedCardId);
        this.cardFormVisibilityChange.emit(this.showCardForm);
      }
    }
    if (
      changes['initialSelectedCardId'] &&
      this.initialSelectedCardId !== null
    ) {
      this.selectedCardId = this.initialSelectedCardId;
    }
    if (
      changes['initialSellerCode'] ||
      changes['initialSellerData'] ||
      changes['isLastStep']
    ) {
      this.applyInitialSellerToView();
    }
  }

  private applyInitialSellerToView(): void {
    const code = this.initialSellerCode ?? this.sellerForm.get('sellerCode')?.value;
    if (code != null && String(code).trim() !== '') {
      const sanitized = String(code).trim().toUpperCase();
      this.sellerAssistance = true;
      this.sellerForm.get('sellerCode')?.setValue(sanitized, { emitEvent: false });
      this.sellerData = this.initialSellerData ?? null;
      this.updateSellerFormValidity();

      // Sem dados pré-carregados, dispara a busca pelo código
      if (!this.sellerData && sanitized.length === 5) {
        this.sellerForm.get('sellerCode')?.setValue(sanitized, {
          emitEvent: true,
        });
      }
    } else if (this.isLastStep) {
      // No resumo final, sem código salvo volta para "Não"
      this.sellerAssistance = false;
      this.sellerForm.get('sellerCode')?.setValue(null, { emitEvent: false });
      this.sellerData = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }

  private hasDSM(cartItems: CartItem[]): boolean {
    if (!cartItems || cartItems.length === 0) {
      return false;
    }

    return cartItems.some((item: CartItem) => {
      const variationIsDSM = (item.selectedVariation as ProductV2Variation)?.isDSM;
      if (variationIsDSM === false) {
        return true;
      }

      return false;
    });
  }

  private hasMIND(cartItems: CartItem[]): boolean {
    if (!cartItems || cartItems.length === 0) {
      return false;
    }
    return cartItems.some((item: CartItem) => item.product?.productType === 'MIND');
  }

  private defaultPaymentMethod(): 'boleto' | 'credit_card' {
    return CREDIT_CARD_PAYMENT_ENABLED ? 'credit_card' : 'boleto';
  }

  private resolvePaymentMethod(
    method: 'boleto' | 'credit_card'
  ): 'boleto' | 'credit_card' {
    if (!CREDIT_CARD_PAYMENT_ENABLED && method === 'credit_card' && !this.isPlanPayment) {
      return 'boleto';
    }
    return method;
  }

  private isCreditCardDisabled(cartItems: CartItem[]): boolean {
    return (
      !CREDIT_CARD_PAYMENT_ENABLED ||
      this.hasDSM(cartItems) ||
      this.hasMIND(cartItems)
    );
  }

  private validateAndAdjustPaymentMethod(cartItems: CartItem[]): void {
    const hasBoletoOnlyItems = this.isCreditCardDisabled(cartItems);

    if (hasBoletoOnlyItems && this.selectedPaymentMethod === 'credit_card' && this.invoicePaymentType === 'boleto_cartao' && !this.isPlanPayment) {
      const shouldChangeAutomatically = !this.isLastStep || (this.isLastStep && this.isEditing);
      
      if (shouldChangeAutomatically) {
        this.selectedPaymentMethod = 'boleto';
        this.paymentOptionService.setSelected('boleto');
        this.showCardForm = false;
        this.cardFormVisibilityChange.emit(this.showCardForm);
        
        if (!(this.isLastStep && this.isEditing)) {
          this.paymentMethodChange.emit(this.selectedPaymentMethod);
        }
      }
    }
  }

  private updatePaymentMethodOptions(cartItems?: CartItem[]): void {
    if (!cartItems) {
      this.cartFacade.getCartItems()
        .pipe(take(1))
        .subscribe((items: CartItem[]) => {
          this.updatePaymentMethodOptionsWithItems(items);
        });
      return;
    }

    this.updatePaymentMethodOptionsWithItems(cartItems);
  }

  private updatePaymentMethodOptionsWithItems(cartItems: CartItem[]): void {
    const hasBoletoOnlyItems = this.isCreditCardDisabled(cartItems);
    const previousHasDSM = this.hasDSMProducts;
    this.hasDSMProducts = this.hasDSM(cartItems) || this.hasMIND(cartItems);

    const allOptions: SlideButtonOption[] = [
      {
        label: this.i18nService.instant('pages.paymentInvoice.payment.boleto'),
        value: 'boleto',
        icon: { name: 'codigo-de-barras', type: 'mondrian-icons' },
      },
      {
        label: this.i18nService.instant('pages.paymentInvoice.payment.creditCard'),
        value: 'credit_card',
        icon: { name: 'cartao-de-credito', type: 'mondrian-icons' },
      },
    ];

    if (hasBoletoOnlyItems && !this.isPlanPayment) {
      this.paymentMethodOptions = allOptions.filter(option => option.value !== 'credit_card');
      this.validateAndAdjustPaymentMethod(cartItems);
    } else {
      this.paymentMethodOptions = allOptions;

      if (previousHasDSM && !this.hasDSMProducts) {
        this.paymentOptionService.setSelected('');
        setTimeout(() => {
          this.paymentOptionService.setSelected('boleto');
        }, 10);
      }
    }
  }

  private captureDeviceInfo(): void {
    this.deviceInfoService.getDeviceInfo().subscribe({
      next: deviceInfo => {
        this.deviceInfo = deviceInfo;
      },
      error: error => {
        console.error('Erro ao capturar informações do dispositivo:', error);
      },
    });
  }

  private setupSalespersonCodeSubscription(): void {
    const codeControl = this.sellerForm.get('sellerCode');
    if (!codeControl) {
      return;
    }

    codeControl.valueChanges
      ?.pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(codeValue => {
        const sanitized = this.sanitizeSellerCode(codeValue ?? '');
        if (codeValue !== sanitized) {
          codeControl.setValue(sanitized, { emitEvent: false });
        }

        codeControl.markAsTouched();
        codeControl.markAsDirty();

        if (!this.sellerAssistance) {
          this.isLoadingSellerCode = false;
          this.sellerData = null;
          this.updateSellerFormValidity();
          return;
        }

        if (sanitized.length === 5) {
          this.isLoadingSellerCode = true;
          this.sellerData = null;
          this.updateSellerFormValidity();
          this.sellerService.getSellerByCode(sanitized).subscribe({
            next: (response: SellerResponse) => {
              timer(500).subscribe(() => {
                this.sellerData = response;
                this.isLoadingSellerCode = false;
                this.updateSellerFormValidity();
              });
            },
            error: () => {
              timer(500).subscribe(() => {
                this.sellerData = null;
                this.isLoadingSellerCode = false;
                this.updateSellerFormValidity();
              });
            },
          });
        } else {
          this.isLoadingSellerCode = false;
          this.sellerData = null;
          this.updateSellerFormValidity();
        }
      });
  }

  private sanitizeSellerCode(value: string): string {
    return value
      ? value
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-zA-Z0-9]/g, '')
          .toUpperCase()
          .substring(0, 5)
      : '';
  }

  private updateSellerFormValidity(): void {
    const sellerCodeControl = this.sellerForm.get('sellerCode');
    if (sellerCodeControl) {
      sellerCodeControl.markAsTouched();
      sellerCodeControl.markAsDirty();

      if (!this.sellerAssistance) {
        sellerCodeControl.setErrors(null);
        return;
      }

      const value = sellerCodeControl.value as string;
      if (!value) {
        sellerCodeControl.setErrors({ required: true });
        return;
      }

      if (this.sellerData?.data) {
        sellerCodeControl.setErrors(null);
      } else {
        sellerCodeControl.setErrors({
          invalidSeller: {
            message: this.isLoadingSellerCode
              ? 'Verificando código...'
              : 'Código do vendedor inválido ou não encontrado',
          },
        });
      }
    }
  }

  onSellerCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cleaned = this.sanitizeSellerCode(input.value || '');

    if (input.value !== cleaned) {
      input.value = cleaned;
    }

    const control = this.sellerForm.get('sellerCode');
    if (control) {
      if (control.value !== cleaned) {
        control.setValue(cleaned);
      }
      control.markAsTouched();
      control.markAsDirty();
    }

    setTimeout(() => {
      input.setSelectionRange(cleaned.length, cleaned.length);
    }, 0);

    if (this.sellerAssistance) {
      this.sellerData = null;
      this.updateSellerFormValidity();
    }
  }

  onSellerCodePaste(event: ClipboardEvent): void {
    event.preventDefault();

    const pasted = this.sanitizeSellerCode(
      event.clipboardData?.getData('text') || ''
    );

    const input = event.target as HTMLInputElement;
    input.value = pasted;

    const control = this.sellerForm.get('sellerCode');
    if (control) {
      control.setValue(pasted);
      control.markAsTouched();
      control.markAsDirty();
    }

    setTimeout(() => {
      input.setSelectionRange(pasted.length, pasted.length);
    }, 0);

    if (this.sellerAssistance) {
      this.sellerData = null;
      this.updateSellerFormValidity();
    }
  }

  onSalespersonChange(value: boolean) {
    this.sellerAssistance = value;
    const sellerCodeControl = this.sellerForm.get('sellerCode');

    if (!value) {
      this.sellerData = null;
      this.isLoadingSellerCode = false;
      sellerCodeControl?.setValue(null, { emitEvent: false });
      sellerCodeControl?.setErrors(null);
      sellerCodeControl?.markAsUntouched();
      sellerCodeControl?.markAsPristine();
    } else {
      this.sellerData = null;
      sellerCodeControl?.setValue('', { emitEvent: true });
      sellerCodeControl?.markAsTouched();
      sellerCodeControl?.markAsDirty();
      this.updateSellerFormValidity();
    }
  }

  onPaymentMethodChange(value: string) {
    const newPaymentMethod = value as 'boleto' | 'credit_card';

    this.selectedPaymentMethod = newPaymentMethod;
    this.paymentOptionService.setSelected(this.selectedPaymentMethod);

    if (value === 'boleto') {
      this.showCardForm = false;
      this.cardFormVisibilityChange.emit(this.showCardForm);
    } else {
      this.selectedCardId = 'new';
      this.showCardForm = false;
      this.cardFormVisibilityChange.emit(this.showCardForm);
      if (!this.hasDSMProducts) {
        this.loadCards();
      }
    }

    if (!(this.isLastStep && this.isEditing)) {
      this.paymentMethodChange.emit(this.selectedPaymentMethod);
      if (value !== 'boleto') {
        this.tokenCardSelected.emit(this.selectedCardId);
      }
    }
  }

  private loadCards(cardTokenToSelect?: string): void {
    if (this.hasDSMProducts) {
      return;
    }

    this.isLoading = true;
    if (!this.userUuid) {
      this.isLoading = false;
      return;
    }

    this.creditCardService.getCards(this.userUuid).subscribe({
      next: response => {
        const cards = response.data ?? [];

        cards.sort((a, b) => b.id - a.id);

        this.cardsInfo = cards;

        if (
          cardTokenToSelect &&
          this.cardsInfo.some(card => card.cardToken === cardTokenToSelect)
        ) {
          this.selectedCardId = cardTokenToSelect;
          this.onCreditCardSelected(this.selectedCardId);
        }

        this.isLoading = false;
      },
      error: error => {
        console.error('Erro ao buscar cartões:', error);
        this.isLoading = false;
      },
    });
  }

  onCreditCardSelected(value: string | 'new') {
    this.selectedCardId = value;
    if (value === 'new') {
      this.showCardForm = true;
    } else {
      this.showCardForm = false;
    }
    this.cardFormVisibilityChange.emit(this.showCardForm);

    if (!(this.isLastStep && this.isEditing)) {
      this.tokenCardSelected.emit(this.selectedCardId);
    }
  }

  onCancelCard() {
    this.selectedCardId = 'new';
    this.showCardForm = false;
    this.cardFormVisibilityChange.emit(this.showCardForm);
  }

  onCardFlagChange(cardFlag: string) {
    this.creditCardForm.get('bandeira')?.setValue(cardFlag);
  }

  onRegisterCard() {
    if (this.creditCardForm.valid && this.userData) {
      this.isLoading = true;
      const _cardRequest: ExternalCreditCardRequest = {
        userUuid: this.userData.uuid || '',
        cliente: {
          identificacaoCliente:
            this.cnpjPipe.removeCnpjMask(this.userData.document || '') || '',
          documento: this.cnpjPipe.removeCnpjMask(this.userData.document || ''),
          telefone: this.userData.phone || '',
          nome:
            this.userData.displayName ||
            `${this.userData.firstName} ${this.userData.lastName}`.trim(),
        },
        cartoes: [
          {
            bandeira: this.creditCardForm.value.bandeira || '',
            cardHolder: this.creditCardForm.value.cardHolder || '',
            cardNumber:
              this.creditCardForm.value.cardNumber.replace(/\D/g, '') || '',
            cardSecurityCode: this.creditCardForm.value.cardSecurityCode || '',
            cardExpirationDate:
              this.creditCardForm.value.cardExpirationDate || '',
            fraud: true,
            fatura: {
              customerIdentity:
                this.cnpjPipe.removeCnpjMask(this.userData.document || '') ||
                '',
              nome:
                this.userData.displayName ||
                `${this.userData.firstName} ${this.userData.lastName}`.trim(),
              endereco: this.userData.addresses[0].address || '',
              complemento: this.userData.addresses[0].complement || '',
              cidade: this.userData.addresses[0].city || '',
              estado: this.userData.addresses[0].state || '',
              codigoPostal: this.userData.addresses[0].zipCode || '',
              pais: 'BR',
              telefone: this.userData.phone || '',
              email: this.userData.email || '',
            },
            antifraude: {
              clienteIP: this.deviceInfo?.ip || null,
              device: this.deviceInfo?.deviceType || null,
              nomeComprador:
                this.userData.displayName ||
                `${this.userData.firstName} ${this.userData.lastName}`.trim(),
              documento:
                this.cnpjPipe.removeCnpjMask(this.userData.document || '') ||
                '',
              telefoneDDD: this.userData.phone.slice(0, 2),
              telefoneNumero: this.userData.phone.slice(2),
              moeda: 'BRL',
              email: this.userData.email || '',
              enderecoEntrega: {
                logradouro: this.userData.addresses[0].address || '',
                numero: this.userData.addresses[0].number || '',
                complemento: this.userData.addresses[0].complement || '',
                cidade: this.userData.addresses[0].city || '',
                uf: this.userData.addresses[0].state || '',
                cep: this.userData.addresses[0].zipCode || '',
                pais: 'BR',
                infoNumero: this.userData.addresses[0].number || '',
                infoComplemento: this.userData.addresses[0].complement || '',
              },
              merchantDefinedData: [
                {
                  mddFieldId: '1',
                  mddFieldValue: this.deviceInfo?.operatingSystem || 'Linux',
                },
                {
                  mddFieldId: '8',
                  mddFieldValue: 'Ustore',
                },
                {
                  mddFieldId: '11',
                  mddFieldValue:
                    this.userData.displayName ||
                    `${this.userData.firstName} ${this.userData.lastName}`,
                },
                {
                  mddFieldId: '12',
                  mddFieldValue: this.cnpjPipe.removeCnpjMask(
                    this.userData.document || ''
                  ),
                },
                {
                  mddFieldId: '17',
                  mddFieldValue: '1',
                },
              ],
            },
          },
        ],
      };
      this.externalCreditCardService.createCreditCard(_cardRequest).subscribe({
        next: response => {
          const newCardToken = response.token;

          this.creditCardForm.reset();
          this.showCardForm = false;
          this.toastService.showSuccess(
            this.i18nService.instant('pages.paymentInvoice.messages.cardSuccess')
          );
          this.cardFormVisibilityChange.emit(this.showCardForm);

          this.selectedCardId = newCardToken;

          this.loadCards(newCardToken);
        },
        error: _error => {
          this.isLoading = false;
          this.toastService.showError(
            this.i18nService.instant('pages.paymentInvoice.messages.cardError')
          );
        },
      });
    } else {
      this.markFormGroupTouched(this.creditCardForm);
      this.toastService.showError(
        this.i18nService.instant('pages.paymentInvoice.messages.formInvalid')
      );
    }
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  onExpiryDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const numbers = value.replace(/\D/g, '');
    const limited = numbers.substring(0, 6);

    let formatted = '';
    if (limited.length <= 2) {
      formatted = limited;
    } else {
      formatted = limited.substring(0, 2) + '/' + limited.substring(2, 6);
    }

    input.value = formatted;
    this.creditCardForm
      .get('cardExpirationDate')
      ?.setValue(formatted, { emitEvent: false });
  }

  onCvcInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const numbers = value.replace(/\D/g, '');
    const limited = numbers.substring(0, 4);

    input.value = limited;
    this.creditCardForm
      .get('cardSecurityCode')
      ?.setValue(limited, { emitEvent: false });
  }

  onCnpjInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const cleanValue = unformatCnpj(value);
    const limitedValue = cleanValue.substring(0, 14);

    if (cleanValue.length > 14) {
      const formatted = this.cnpjPipe.transform(limitedValue);
      input.value = formatted;
      this.creditCardForm
        .get('document')
        ?.setValue(formatted, { emitEvent: true });
      return;
    }

    const formatted = this.cnpjPipe.transform(value);

    input.value = formatted;
    this.creditCardForm
      .get('document')
      ?.setValue(formatted, { emitEvent: true });
  }

  onCnpjPaste(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text') || '';
    const cleanValue = unformatCnpj(pastedText);

    if (cleanValue.length > 14) {
      event.preventDefault();
      const documentControl = this.creditCardForm.get('document');
      if (documentControl) {
        documentControl.setErrors({ invalidCNPJ: true });
        documentControl.markAsTouched();
      }
      return;
    }

    event.preventDefault();
    const formatted = this.cnpjPipe.transform(cleanValue);

    const input = event.target as HTMLInputElement;
    input.value = formatted;
    this.creditCardForm
      .get('document')
      ?.setValue(formatted, { emitEvent: true });
  }
  blockLeadingSpace(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;

    if (event.key === ' ' && input.selectionStart === 0) {
      event.preventDefault();
    }
  }

  onCardNameInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const cleaned = value.replace(/[^a-zA-Z\s]/g, '');
    const upperCased = cleaned.toUpperCase();
    const limited = upperCased.substring(0, 64);

    if (input.value !== limited) {
      input.value = limited;
    }

    this.creditCardForm
      .get('cardHolder')
      ?.setValue(limited, { emitEvent: false });
  }

  editPayment(): void {
    // Se estiver no último step, salvar os valores atuais como originais (para restaurar ao cancelar)
    if (this.isLastStep) {
      this.originalSelectedPaymentMethod = this.selectedPaymentMethod;
      this.originalSelectedCardId = this.selectedCardId;
      this.originalSellerAssistance = this.sellerAssistance;
      this.originalSellerCode =
        this.sellerForm.get('sellerCode')?.value || null;
      this.originalSellerData = this.sellerData;
    }
    this.isEditing = true;
    this.editingStateChange.emit(this.isEditing);
  }

  cancelEdit(): void {
    if (this.isLastStep) {
      if (this.originalSelectedPaymentMethod !== null) {
        this.selectedPaymentMethod = this.originalSelectedPaymentMethod;
      }
      if (this.originalSelectedCardId !== null) {
        this.selectedCardId = this.originalSelectedCardId;
        this.showCardForm = false;
      }
      if (this.originalSellerAssistance !== null) {
        this.sellerAssistance = this.originalSellerAssistance;
      }
      if (this.originalSellerCode !== null) {
        this.sellerForm
          .get('sellerCode')
          ?.setValue(this.originalSellerCode, { emitEvent: false });
      } else {
        this.sellerForm.get('sellerCode')?.setValue(null, { emitEvent: false });
      }
      if (this.originalSellerData !== null) {
        this.sellerData = this.originalSellerData;
      } else {
        this.sellerData = null;
      }
      this.updateSellerFormValidity();

      this.originalSelectedPaymentMethod = null;
      this.originalSelectedCardId = null;
      this.originalSellerAssistance = null;
      this.originalSellerCode = null;
      this.originalSellerData = null;
    }
    this.isEditing = false;
    this.editingStateChange.emit(this.isEditing);
  }

  saveAndContinue(): void {
    // Se estiver no último step, emitir eventos com os valores atuais (que foram modificados durante edição)
    if (this.isLastStep && this.isEditing) {
      this.paymentMethodChange.emit(this.selectedPaymentMethod);
      this.tokenCardSelected.emit(this.selectedCardId);

      // Limpar valores originais após salvar
      this.originalSelectedPaymentMethod = null;
      this.originalSelectedCardId = null;
      this.originalSellerAssistance = null;
      this.originalSellerCode = null;
      this.originalSellerData = null;
    }

    this.isEditing = false;
    this.editingStateChange.emit(this.isEditing);
    if (!this.isLastStep) {
      this.nextStep.emit();
    }
  }

  getSellerAssistanceLabel(): string {
    return this.sellerAssistance 
      ? this.i18nService.instant('pages.paymentInvoice.seller.yes') 
      : this.i18nService.instant('pages.paymentInvoice.seller.no');
  }

  getPaymentMethodLabel(): string {
    if (this.invoicePaymentType === 'minha_conta') {
      return this.i18nService.instant('pages.paymentInvoice.payment.myAccount');
    }
    return this.selectedPaymentMethod === 'boleto'
      ? this.i18nService.instant('pages.paymentInvoice.payment.boleto')
      : this.i18nService.instant('pages.paymentInvoice.payment.creditCard');
  }

  getSelectedCard(): GetCardsData | null {
    if (this.selectedCardId === 'new' || !this.selectedCardId) {
      return null;
    }
    return (
      this.cardsInfo.find(card => card.cardToken === this.selectedCardId) ||
      null
    );
  }

  getSelectedCardFlag(): cardFlag {
    const card = this.getSelectedCard();
    if (!card) {
      return 'UNKNOWN';
    }
    const flag = card.cardFlag.toUpperCase();
    if (flag === 'VISA' || flag === 'MASTERCARD' || flag === 'AMEX') {
      return flag as cardFlag;
    }
    return 'UNKNOWN';
  }

  get showCheck(): boolean {
    if (this.isLastStep && this.isEditing) {
      if (this.invoicePaymentType === 'minha_conta') {
        const isSellerFormValid = this.sellerAssistance
          ? this.sellerForm.valid
          : true;
        return !this.sellerAssistance || isSellerFormValid;
      }

      const isSellerFormValid = this.sellerAssistance
        ? this.sellerForm.valid
        : true;

      if (this.sellerAssistance && !isSellerFormValid) {
        return false;
      }

      if (this.selectedPaymentMethod === 'boleto') {
        return true;
      }

      if (this.selectedPaymentMethod === 'credit_card') {
        if (this.selectedCardId !== 'new') {
          return true;
        }

        if (this.showCardForm) {
          return this.creditCardForm.valid;
        }

        return false;
      }

      return false;
    }

    if (this.isLastStep && !this.isEditing) {
      return true;
    }

    if (this.invoicePaymentType === 'minha_conta') {
      const isSellerFormValid = this.sellerAssistance
        ? this.sellerForm.valid
        : true;
      return !this.sellerAssistance || isSellerFormValid;
    }

    const isSellerFormValid = this.sellerAssistance
      ? this.sellerForm.valid
      : true;

    if (this.sellerAssistance && !isSellerFormValid) {
      return false;
    }

    if (this.selectedPaymentMethod === 'boleto') {
      return true;
    }

    if (this.selectedPaymentMethod === 'credit_card') {
      if (this.selectedCardId !== 'new') {
        return true;
      }

      if (this.showCardForm) {
        return this.creditCardForm.valid;
      }

      return false;
    }

    return false;
  }
}
